import { useEffect } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { AlertTriangle, ArrowUpRight, Eye, EyeOff, Languages, Layers, Palette, Plus, Rocket, UtensilsCrossed, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAdminLang } from "./context";
import AnalyticsSection from "./analytics/AnalyticsSection";

const Stat = ({ icon: Icon, label, value, testid, tone = "text-primary", sub }) => (
  <div className="rounded-2xl border bg-card p-5" data-testid={testid}>
    <div className="flex items-center justify-between text-muted-foreground">
      <span className="text-sm">{label}</span>
      <Icon className={`h-4 w-4 ${tone}`} />
    </div>
    <div className="mt-3 text-3xl font-extrabold tabular-nums" data-testid={`${testid}-value`}>{value ?? "–"}</div>
    {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

const Shortcut = ({ to, icon: Icon, label, testid, external }) => {
  const cls = "group flex items-center justify-between rounded-2xl border bg-card p-4 transition-colors hover:border-primary/60";
  const body = (<><span className="flex items-center gap-3 text-sm font-semibold"><span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/15 text-primary"><Icon className="h-4 w-4" /></span>{label}</span><ArrowUpRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:-translate-y-0.5 rtl:-scale-x-100" /></>);
  return external ? <a href={to} target="_blank" rel="noreferrer" className={cls} data-testid={testid}>{body}</a> : <Link to={to} className={cls} data-testid={testid}>{body}</Link>;
};

export default function Overview() {
  const { t, lang } = useAdminLang();
  const { stats: s, refresh, publish, publishing } = useOutletContext();
  useEffect(() => {
    refresh();
  }, [refresh]);
  const when = s?.published_at ? new Date(s.published_at).toLocaleString(lang === "ar" ? "ar-SA-u-nu-latn" : "en-GB") : null;

  return (
    <div className="space-y-8" data-testid="admin-overview">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">{t("ov.title")}</h1>
        <p className="mt-1 text-muted-foreground">{t("ov.subtitle")}</p>
      </div>
      {s?.sample_items > 0 && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="sample-notice">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{t("ov.sampleNotice", { n: s.sample_items })}
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat icon={Layers} label={t("ov.categories")} value={s?.categories} testid="stat-categories" sub={s?.hidden_categories ? t("ov.hiddenCats", { n: s.hidden_categories }) : null} />
        <Stat icon={UtensilsCrossed} label={t("ov.items")} value={s?.items} testid="stat-items" />
        <Stat icon={Eye} label={t("ov.visible")} value={s?.visible_items} testid="stat-visible" tone="text-emerald-400" />
        <Stat icon={EyeOff} label={t("ov.hidden")} value={s?.hidden_items} testid="stat-hidden" tone="text-zinc-400" />
        <Stat icon={XCircle} label={t("ov.unavailable")} value={s?.unavailable_items} testid="stat-unavailable" tone="text-red-400" />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4 rounded-2xl border bg-card p-6" data-testid="translation-card">
          <div className="flex items-center gap-2 font-semibold"><Languages className="h-4 w-4 text-primary" />{t("ov.translation")}</div>
          <div className="text-4xl font-extrabold tabular-nums" data-testid="translation-pct">{s?.translation_pct ?? "–"}%</div>
          <Progress value={s?.translation_pct || 0} />
          {(s?.incomplete_items > 0 || s?.incomplete_categories > 0) && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">{t("ov.incomplete", { n: s.incomplete_items, c: s.incomplete_categories })}</p>
              <Button asChild size="sm" variant="secondary" className="rounded-full"><Link to="/admin/items?tr=missing" data-testid="fix-translations-link">{t("ov.fixTranslations")}</Link></Button>
            </div>
          )}
        </div>
        <div className="space-y-4 rounded-2xl border bg-card p-6" data-testid="publishing-card">
          <div className="flex items-center gap-2 font-semibold"><Rocket className="h-4 w-4 text-primary" />{t("ov.publishing")}</div>
          <div className={`text-lg font-bold ${s?.dirty ? "text-amber-300" : "text-emerald-300"}`}>{s?.dirty ? t("status.unpublished") : t("status.live")}</div>
          <p className="text-sm text-muted-foreground">{when ? t("status.lastPublished", { time: when }) : t("status.never")}</p>
          <p className="text-sm text-muted-foreground">{t("ov.publishHint")}</p>
          <Button onClick={publish} disabled={!s?.dirty || publishing} className="rounded-full" data-testid="overview-publish-btn"><Rocket className="h-4 w-4" />{t("top.publish")}</Button>
        </div>
      </div>
      <div className="space-y-3">
        <h2 className="text-lg font-bold">{t("ov.shortcuts")}</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Shortcut to="/admin/items?new=1" icon={Plus} label={t("ov.addItem")} testid="shortcut-add-item" />
          <Shortcut to="/admin/categories?new=1" icon={Layers} label={t("ov.addCategory")} testid="shortcut-add-category" />
          <Shortcut to="/admin/designer" icon={Palette} label={t("ov.customize")} testid="shortcut-designer" />
          <Shortcut to="/preview" icon={Eye} label={t("ov.previewMenu")} testid="shortcut-preview" external />
        </div>
      </div>
      <AnalyticsSection t={t} lang={lang} />
    </div>
  );
}
